/**
 * SuperAdmin → Alertas por correo: a quién avisar, qué avisar y desde qué empresa se envía.
 * Cada 10 minutos se revisan todas las cuentas y se manda UN correo con lo nuevo.
 */
import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { BellRing, Mail, Send, Save, X, Plus, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import SuperadminLayout from '../components/SuperadminLayout';
import { panelApi } from '../utils/api';
import { Encabezado, Panel, Boton, Cargando, Aviso, Campo, inputCls, fFechaHora, hace } from '../components/ui';

const AYUDA_TIPOS = {
  rechazos: 'Cada comprobante que Hacienda rechace, con el motivo explicado.',
  sin_respuesta: 'Comprobantes enviados que llevan más de 2 horas sin respuesta de Hacienda.',
  certificados: 'Certificados vencidos o que vencen en 30 días (se recuerda una vez por semana).',
  respaldos: 'Cualquier respaldo que falle, de la cuenta o de una empresa.',
  esquema: 'Una base cambió (tabla nueva) y el respaldo por empresa dejó de cubrirla: restaurar una empresa la dejaría inconsistente.'
};

export default function Alertas() {
  const [cfg, setCfg] = useState(null);
  const [remitentes, setRemitentes] = useState([]);
  const [tipos, setTipos] = useState({});
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [probando, setProbando] = useState(false);
  const [aviso, setAviso] = useState(null);
  const [nuevo, setNuevo] = useState('');

  const cargar = async () => {
    try {
      const r = await panelApi.alertas();
      setCfg(r.data.config);
      setRemitentes(r.data.remitentes || []);
      setTipos(r.data.tipos || {});
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo cargar la configuración' });
    } finally {
      setCargando(false);
    }
  };
  useEffect(() => { cargar(); }, []);

  const agregar = () => {
    const correos = nuevo.split(/[,;\s]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    const malos = correos.filter(c => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(c));
    if (malos.length) { setAviso({ tipo: 'error', texto: `Correo inválido: ${malos.join(', ')}` }); return; }
    setCfg({ ...cfg, destinatarios: [...new Set([...cfg.destinatarios, ...correos])] });
    setNuevo('');
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await panelApi.guardarAlertas({ enabled: cfg.enabled, destinatarios: cfg.destinatarios, tipos: cfg.tipos, remitente: cfg.remitente });
      setCfg(r.data.config);
      setAviso({ tipo: 'ok', texto: 'Alertas guardadas' });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo guardar' });
    } finally {
      setGuardando(false);
    }
  };

  const probar = async () => {
    setProbando(true);
    try {
      const r = await panelApi.probarAlertas();
      setAviso({ tipo: 'ok', texto: r.data.message });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo enviar la prueba' });
    } finally {
      setProbando(false);
    }
  };

  const claveRem = (r) => r ? `${r.tenantId}:${r.emisorId}` : '';
  const remActual = remitentes.find(r => claveRem(r) === claveRem(cfg?.remitente));

  return (
    <SuperadminLayout>
      <Encabezado titulo="Alertas por correo" subtitulo="Cada 10 minutos se revisan todas las cuentas y se envía un solo correo con lo nuevo. Lo ya avisado no se repite." />
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />

      {cargando ? <Cargando /> : cfg && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Panel titulo="A quién y desde dónde" icono={Mail}>
            <label className="flex items-center gap-3 p-3 rounded-lg border border-gray-200 mb-4 cursor-pointer">
              <input type="checkbox" className="h-4 w-4" checked={cfg.enabled} onChange={e => setCfg({ ...cfg, enabled: e.target.checked })} />
              <span>
                <span className="font-medium text-gray-900">Enviar alertas por correo</span>
                <span className="block text-xs text-gray-500">{cfg.enabled ? 'Activas' : 'Desactivadas: no se envía nada'}</span>
              </span>
            </label>

            <Campo label="Destinatarios">
              <div className="flex gap-2">
                <input className={inputCls} value={nuevo} onChange={e => setNuevo(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); agregar(); } }}
                  placeholder="soporte@empresa.com (separe varios con coma)" />
                <Boton icono={Plus} onClick={agregar} disabled={!nuevo.trim()}>Agregar</Boton>
              </div>
            </Campo>
            <div className="flex flex-wrap gap-2 mt-3 min-h-[2rem]">
              {cfg.destinatarios.length === 0 && <span className="text-sm text-gray-500">Sin destinatarios todavía.</span>}
              {cfg.destinatarios.map(c => (
                <span key={c} className="inline-flex items-center gap-1.5 pl-3 pr-1.5 py-1 rounded-full bg-blue-50 text-blue-800 text-sm border border-blue-200">
                  {c}
                  <button onClick={() => setCfg({ ...cfg, destinatarios: cfg.destinatarios.filter(x => x !== c) })} className="p-0.5 rounded-full hover:bg-blue-100" title="Quitar"><X className="h-3.5 w-3.5" /></button>
                </span>
              ))}
            </div>

            <Campo label="Se envía desde el correo de" className="mt-5"
              ayuda="Se usa la configuración de correo (Office 365 o SMTP) que ya tiene esa empresa.">
              <select className={inputCls} value={claveRem(cfg.remitente)}
                onChange={e => { const r = remitentes.find(x => claveRem(x) === e.target.value); setCfg({ ...cfg, remitente: r ? { tenantId: r.tenantId, emisorId: r.emisorId } : null }); }}>
                <option value="">Elija una empresa…</option>
                {remitentes.map(r => <option key={claveRem(r)} value={claveRem(r)}>{r.empresa} · {r.correo} ({r.cuenta})</option>)}
              </select>
            </Campo>
            {remitentes.length === 0 && <p className="text-sm text-amber-700 mt-2">Ninguna empresa tiene el correo configurado.</p>}
            {remActual && <p className="text-xs text-gray-500 mt-2">Remitente: {remActual.correo} · {remActual.proveedor === 'smtp' ? 'SMTP' : 'Office 365'}</p>}
          </Panel>

          <div className="space-y-6">
            <Panel titulo="Qué avisar" icono={BellRing}>
              <div className="space-y-2">
                {Object.entries(tipos).map(([k, label]) => (
                  <label key={k} className={clsx('flex items-start gap-3 p-3 rounded-lg border cursor-pointer', cfg.tipos[k] ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200')}>
                    <input type="checkbox" className="h-4 w-4 mt-0.5" checked={!!cfg.tipos[k]} onChange={e => setCfg({ ...cfg, tipos: { ...cfg.tipos, [k]: e.target.checked } })} />
                    <span>
                      <span className="font-medium text-gray-900 text-sm">{label}</span>
                      <span className="block text-xs text-gray-500">{AYUDA_TIPOS[k]}</span>
                    </span>
                  </label>
                ))}
              </div>
            </Panel>

            <Panel titulo="Estado" icono={Clock}>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div><dt className="text-gray-500 text-xs">Última revisión</dt><dd className="text-gray-900" title={fFechaHora(cfg.ultimaRevision)}>{cfg.ultimaRevision ? hace(cfg.ultimaRevision) : '—'}</dd></div>
                <div><dt className="text-gray-500 text-xs">Último correo enviado</dt><dd className="text-gray-900" title={fFechaHora(cfg.ultimoEnvio)}>{cfg.ultimoEnvio ? hace(cfg.ultimoEnvio) : 'Nunca'}</dd></div>
              </dl>
              {cfg.ultimoError
                ? <p className="mt-3 text-sm text-red-700 flex items-start gap-2"><AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />Último error: {cfg.ultimoError}</p>
                : cfg.ultimoEnvio && <p className="mt-3 text-sm text-emerald-700 flex items-center gap-2"><CheckCircle2 className="h-4 w-4" />Enviando sin errores</p>}
            </Panel>

            <div className="flex flex-wrap justify-end gap-2">
              <Boton icono={Send} cargando={probando} onClick={probar} title="Envía a los destinatarios guardados un correo con lo que hoy está abierto">Enviar correo de prueba</Boton>
              <Boton variante="primario" icono={Save} cargando={guardando} onClick={guardar}>Guardar</Boton>
            </div>
          </div>
        </div>
      )}
    </SuperadminLayout>
  );
}
